import { Icon } from 'components';
import { formatDate } from 'utils/date';
import { formatPrice } from 'utils/helpers';
import { EventDashboardStatistics } from 'store/api/event';

import { GetSalesOverviewInfo } from './types';

// Missing sales (sales never rung up in the register) are one tile with the
// same total as the Missing sales tab: every row of the event, refunds and
// lost chargebacks deducted, whatever the dashboard's date range (a refund
// dated after the show still belongs to its show). Shown only to the roles
// that read Missing sales, also when the event has no register sales.
export const getMissingSalesInfo = ({
  theme,
  total,
}: {
  total: number;
  theme: GetSalesOverviewInfo['theme'];
}) => ({
  icon: <Icon.Bank />,
  title: 'Missing sales',
  value: formatPrice(total),
  iconCardStyles: {
    icon: theme.colors.blue.primary.standard,
    background: theme.colors.blue.tertiary.standard,
  },
});

export const getSalesOverviewInfo = ({ data, theme }: GetSalesOverviewInfo) => [
  // Order (owner 2026-10-15): the sales count, then gross, its deductions,
  // and net last.
  {
    title: 'Event Total Sales',
    value: data?.checkoutCounts,
    icon: <Icon.CoinDollarNotFilled />,
    iconCardStyles: {
      icon: theme.colors.pigmentIndigo.primary.standard,
      background: theme.colors.pigmentIndigo.tertiary.standard,
    },
  },
  {
    icon: <Icon.Bank />,
    title: 'Gross Sales',
    value: formatPrice(data?.grossSales),
    iconCardStyles: {
      icon: theme.colors.blue.primary.standard,
      background: theme.colors.blue.tertiary.standard,
    },
  },
  {
    title: 'Refunds',
    icon: <Icon.Delivery />,
    value: data?.refund && formatPrice(data?.refund),
    iconCardStyles: {
      icon: theme.colors.red.primary.standard,
      background: theme.colors.red.tertiary.standard,
    },
  },
  {
    title: 'Sales Tax',
    icon: <Icon.Banknote />,
    value: formatPrice(data?.salesTax),
    iconCardStyles: {
      icon: theme.colors.yellow.primary.standard,
      background: theme.colors.yellow.tertiary.standard,
    },
  },
  {
    icon: <Icon.Wallet />,
    title: 'Processing Fees',
    value: formatPrice(data?.processingFee),
    iconCardStyles: {
      icon: theme.colors.orange.primary.standard,
      background: theme.colors.orange.tertiary.standard,
    },
  },
  {
    title: 'Tips',
    icon: <Icon.Billing />,
    value: data?.totalTips && formatPrice(data.totalTips),
    iconCardStyles: {
      icon: theme.colors.purple.primary.standard,
      background: theme.colors.purple.tertiary.standard,
    },
  },
  {
    title: 'Shipping',
    icon: <Icon.Delivery />,
    value: data?.shipping && formatPrice(data?.shipping),
    iconCardStyles: {
      icon: theme.colors.green.primary.standard,
      background: theme.colors.green.tertiary.standard,
    },
  },
  {
    title: 'Net Sales',
    icon: <Icon.Coins />,
    value: formatPrice(data?.netSales),
    iconCardStyles: {
      icon: theme.colors.blue.primary.standard,
      background: theme.colors.blue.tertiary.standard,
    },
  },
];

export const getStockInfo = ({ data, theme }: GetSalesOverviewInfo) => [
  {
    title: 'PRP',
    icon: <Icon.SecondaryList />,
    value:
      data?.productRevenuePercentage && `${data?.productRevenuePercentage}%`,
    iconCardStyles: {
      icon: theme.colors.blue.primary.standard,
      background: theme.colors.blue.tertiary.standard,
    },
  },
  {
    title: 'Item Sold',
    icon: <Icon.Boxes />,
    value: data?.totalProducts,
    iconCardStyles: {
      icon: theme.colors.purple.primary.standard,
      background: theme.colors.purple.tertiary.standard,
    },
  },
];

export const getWastageProductsInfo = ({
  data,
  theme,
}: GetSalesOverviewInfo) => [
  {
    title: 'No. Items',
    icon: <Icon.ShoppingCart />,
    value: data?.damagedProducts?.totalCount,
    iconCardStyles: {
      icon: theme.colors.blue.primary.standard,
      background: theme.colors.blue.tertiary.standard,
    },
  },
  {
    title: 'Loss',
    icon: <Icon.CoinDollarNotFilled />,
    value:
      data?.damagedProducts?.totalCost &&
      formatPrice(data?.damagedProducts?.totalCost),
    iconCardStyles: {
      icon: theme.colors.red.primary.standard,
      background: theme.colors.red.tertiary.standard,
    },
  },
];

export const prepareChartData = (
  data?: EventDashboardStatistics['dailySales']
) => [
  {
    id: 'Total Amount',
    data: data
      ? data.map((sale) => ({
          y: sale.sales,
          x: formatDate(sale.date),
        }))
      : [],
  },
];
